import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEndpoints } from './useEndpoints';
import { IEvent } from '../models/event';
import { IWorkout } from '../models/programming';
import { Paginated } from '../typings/Paginated';

/** Shape returned by GET /assignments/:id/occurrences/:eventId */
export type IOccurrence = IEvent & {
  workout?: IWorkout;
};

export function useOccurrencesList(assignmentId: string | undefined, enabled = true) {
  const { getAssignmentOccurrences } = useEndpoints();
  return useQuery<IOccurrence[]>({
    queryKey: ['occurrences', assignmentId],
    queryFn: async () => {
      const { data } = await getAssignmentOccurrences(assignmentId!);
      return (data as Paginated<IOccurrence> | undefined)?.docs ?? (data as IOccurrence[] | undefined) ?? [];
    },
    enabled: enabled && !!assignmentId,
    staleTime: 60_000,
  });
}

export function useOccurrence(assignmentId: string | undefined, eventId: string | undefined, enabled = true) {
  const { getAssignmentOccurrence } = useEndpoints();
  return useQuery<IOccurrence>({
    queryKey: ['occurrence', assignmentId, eventId],
    queryFn: async () => {
      const { data } = await getAssignmentOccurrence(assignmentId!, eventId!);
      return data as any;
    },
    enabled: enabled && !!assignmentId && !!eventId,
    staleTime: 0,
  });
}

export function useUpdateOccurrence() {
  const { patchAssignmentOccurrence } = useEndpoints();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ assignmentId, eventId, scope, body }: {
      assignmentId: string; eventId: string;
      scope: 'single' | 'this_and_future' | 'all';
      body: any;
    }) => {
      const { data } = await patchAssignmentOccurrence(assignmentId, eventId, scope, body);
      return data;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['occurrences', vars.assignmentId] });
      qc.invalidateQueries({ queryKey: ['occurrence', vars.assignmentId, vars.eventId] });
      qc.invalidateQueries({ queryKey: ['events'] });
    },
  });
}

export function useDeleteOccurrence() {
  const { deleteAssignmentOccurrence } = useEndpoints();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ assignmentId, eventId, scope }: {
      assignmentId: string; eventId: string;
      scope: 'single' | 'this_and_future' | 'all';
    }) => {
      await deleteAssignmentOccurrence(assignmentId, eventId, scope);
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['occurrences', vars.assignmentId] });
      qc.invalidateQueries({ queryKey: ['events'] });
      qc.invalidateQueries({ queryKey: ['programming-assignments'] });
    },
  });
}
